import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import gql from 'graphql-tag';
import {
  StateService,
  UpdateConnextMutation,
  SystemsService,
  FwBuild,
  wait,
  System,
  generateUUID,
  Device,
  cmpConnextId,
  ProductModelService,
  UpdateUserInConnextMutation,
} from 'ngx-core-services';
import { UiPromiseService, DialogDataComponent } from 'ngx-zcs-components';
import { first } from 'rxjs/operators';

import { PositionDetails } from '../components/maps-form/maps-form.models';
import { BlockTranslationsService } from '../../block-translations.service';

@Injectable({
  providedIn: 'root',
})
export class CreateFirebaseBlockService {

  readonly systems$ = this.systemsService.systems$;

  constructor(
    private readonly blockTranslationsService: BlockTranslationsService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly stateService: StateService,
    private readonly uiPromiseService: UiPromiseService,
    private readonly updateConnextMutation: UpdateConnextMutation,
    private readonly updateUserInConnextMutation: UpdateUserInConnextMutation,
    readonly systemsService: SystemsService,
  ) {
    this.updateConnextFw = this.updateConnextFw.bind(this);
    this.createFirebasePlant = this.createFirebasePlant.bind(this);
    this.createConnextPlant = this.createConnextPlant.bind(this);
    this.createVirtualPlant = this.createVirtualPlant.bind(this);
    this.updateUserInConnext = this.updateUserInConnext.bind(this);
  }

  updateConnextFw(connextUid: string, afterUpdateRequest: any, labels: { loadingMessage: string, fwUpdateTitle: string, fwUpdateContent: string }) {
    const force = true; /** [Upgrade] TODO: Why this is necessary? */
    const timeoutBeforeReCheck = 1000 * 15;

    const query = gql`
    fragment connextupdateresponse on ConnextUpdateResponse {
      success
    }`;

    const upgradeRequest = () => this.updateConnextMutation.execute(query, {
      force,
      deviceId: connextUid,
      build: FwBuild.ANY,
      compatibleWith: 'AzzurroMonitoring',
    });

    return () => {
      const dialogRef = this.dialog.open(DialogDataComponent, {
        width: '600px',
      });
      const primaryAction = async () => {
        await upgradeRequest();
        await wait(timeoutBeforeReCheck);
        afterUpdateRequest();
      };
      const actions = [
        {
          text: 'Cancel',
          action: () => dialogRef.close(),
        },
        {
          text: 'Yes',
          focus: true,
          action: () => {
            this.uiPromiseService.run(primaryAction, {
              loading: true,
              loadingMessage: labels.loadingMessage,
            });

            dialogRef.close();
          },
        },
      ];

      dialogRef.componentInstance.data = {
        title: labels.fwUpdateTitle,
        content: labels.fwUpdateContent,
        buttons: actions,
      };
    };
  }

  createFirebasePlant(name: string, config: {
    uid?: string;
    connextId?: string;
    virtualDevices?: string[];
    coordinates?: PositionDetails;
    waitBeforeRedirect?: number;
  }) {
    const waitBeforeRedirectWithConnext = 15 * 1000;
    const waitBeforeRedirectWithVirtual = 3 * 1000;
    const waitBeforeRedirect = config.waitBeforeRedirect ||
    (config.connextId ? waitBeforeRedirectWithConnext : waitBeforeRedirectWithVirtual);
    const promiseTimeout = waitBeforeRedirect + 15 * 1000;
    return () => this.uiPromiseService.run(async () => {
      const currentSystem = config.connextId
        ? (await this.createConnextPlant(name, config.connextId, config.uid))
        : (await this.createVirtualPlant(name, config.virtualDevices || [], config.coordinates!, config.uid));
        await wait(waitBeforeRedirect);

      return currentSystem;
    }, {
      loading: true,
      loadingMessage: this.blockTranslationsService.labels.commonUpdatingSystemConfiguration,
      timeout: promiseTimeout,
    });
  }

  async createConnextPlant(name: string, connextId: string, uid: string = generateUUID()) {
    const currentSystemList = (await this.systems$.pipe(first()).toPromise()) || [];
    const preExistingSystem = currentSystemList?.find((system) => cmpConnextId(system.configuration.connextId, connextId));

    const connextDevice: Device ={
      type: 'connext',
      id: connextId,
      name,
    };

    const currentSystem: System = {
      ...preExistingSystem,
      configuration: {
        connextId,
        temporaryZoneList: [],
      },
      img: '',
      devices: [
        connextDevice,
      ],
      name,
      uid: preExistingSystem?.uid ?? uid,
      source: 'firebase',
    };

    await this.stateService.saveCurrentSystem(currentSystem);

    return currentSystem;
  }

  async createVirtualPlant(name: string, virtualDevices: string[], loc: PositionDetails, uid: string = generateUUID()) {
    if (!loc) {
      throw new Error('Location is required for virtual plant');
    }

    const locationLat = `${loc.latLng.lat().toPrecision(7)}`;
    const locationLng = `${loc.latLng.lng().toPrecision(7)}`;
    const coordinates = `${locationLat}, ${locationLng}`;
    /**
     * Timezone is in quarter hours
     */
    const tz = (Number(loc.timezone?.gmtOffset) / 3600) * 4;

    const devices: Device[] = virtualDevices.map((id) => {
      id = id.toUpperCase().trim();
      const product = new ProductModelService(id);
      const type = product.type?.name || 'inverter';

      return {
        type,
        id,
        name: id,
      };
    });

    const currentSystem: System = {
      configuration: {
        activeZone: {
          coordinates,
          tz,
        },
        temporaryZoneList: [{
          coordinates,
          tz,
        }],
      },
      img: '',
      devices,
      name,
      uid,
      source: 'firebase',
    };

    await this.stateService.saveCurrentSystem(currentSystem);

    return currentSystem;
  }

  updateUserInConnext(email: string, userLevel: number, uid: string, loadingMessage: string, remove = false) {
    const userQuery = gql`
      fragment connextuser on ConnextUser {
        email
        operator
        userLevel
      }
    `;
    return this.uiPromiseService.run(async () => this.updateUserInConnextMutation.execute(userQuery, {
      deviceId: uid,
      email,
      userLevel,
      remove,
    }), {
      loading: true,
      loadingMessage
    });
  }
}
